import { useState } from 'react';
import {
  Table,
  Flex,
  IconButton,
  Link,
  Tooltip,
  Button,
} from '@radix-ui/themes';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { EditableInput } from './EditableInput.jsx';
import { CategorySelect } from './CategorySelect.jsx';
import { VendorSelect } from './VendorSelect.jsx';
import { DeleteFile } from './DeleteFile.jsx';
import { updateVaultFile } from '../data';
import { useNotification } from '../../../shared/Notification/useNotification';

export const FileRow = ({
  file,
  isEditing,
  setEditingId,
  categories,
  vendors,
}) => {
  const { t } = useTranslation('vault');
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();

  const [editName, setEditName] = useState(file.name);
  const [editCategoryId, setEditCategoryId] = useState(file.vaultCategoryId);
  const [editVendors, setEditVendors] = useState(() =>
    (file.vendorNames || []).map(name => ({ value: name, label: name })),
  );
  const [isSaving, setIsSaving] = useState(false);

  const fileExtension = file?.nameWithExtension
    ?.split('.')
    .pop()
    ?.toLowerCase();
  const openInNewTabExtensions = ['pdf'];
  const shouldOpenInNewTab = openInNewTabExtensions.includes(fileExtension);
  const linkProps = shouldOpenInNewTab
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : { download: file.nameWithExtension };

  const categoryLabel = file.category || t('noCategoryAssigned');

  const handleDiscard = () => {
    setEditName(file.name);
    setEditCategoryId(file.vaultCategoryId);
    setEditVendors(
      (file.vendorNames || []).map(name => ({ value: name, label: name })),
    );
    setEditingId(null);
  };

  const handleSave = async () => {
    if (editName.trim() === '') {
      showError({ content: t('fileNameEmpty') });
      return;
    }

    const data = {};
    let hasChanges = false;

    if (editName.trim() !== file.name) {
      data.displayName = editName.trim();
      hasChanges = true;
    }

    if (String(editCategoryId ?? '') !== String(file.vaultCategoryId ?? '')) {
      data.vaultCategoryId = editCategoryId;
      hasChanges = true;
    }

    const currentVendorNames = (file.vendorNames || []).sort().join(',');
    const newVendorNames = editVendors
      .map(o => o.label)
      .sort()
      .join(',');
    if (currentVendorNames !== newVendorNames) {
      data.vendorNames = editVendors.map(o => o.label);
      hasChanges = true;
    }

    if (!hasChanges) {
      setEditingId(null);
      return;
    }

    setIsSaving(true);
    try {
      await updateVaultFile({ vaultFileId: file.id, ...data });
      await queryClient.invalidateQueries(['vaultFiles']);
      showSuccess({ content: t('fileUpdated') });
      setEditingId(null);
    } catch {
      showError({ content: t('fileUpdateFailed') });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Table.Row align="center" key={file.id}>
      <Table.RowHeaderCell>
        {isEditing ? (
          <EditableInput value={editName} onChange={setEditName} />
        ) : (
          <Flex maxWidth="250px">
            <Link href={file.url} weight="medium" truncate {...linkProps}>
              {file.nameWithExtension}
            </Link>
          </Flex>
        )}
      </Table.RowHeaderCell>
      <Table.Cell>
        {isEditing ? (
          <CategorySelect
            categories={categories}
            value={editCategoryId}
            onChange={setEditCategoryId}
          />
        ) : (
          categoryLabel
        )}
      </Table.Cell>
      <Table.Cell>
        {isEditing ? (
          <VendorSelect
            vendors={vendors}
            value={editVendors}
            onChange={setEditVendors}
          />
        ) : file.vendorNames?.length > 0 ? (
          file.vendorNames.join(', ')
        ) : (
          t('noVendorAssigned')
        )}
      </Table.Cell>
      <Table.Cell>{file.createdAt}</Table.Cell>
      <Table.Cell>{file.createdBy}</Table.Cell>
      <Table.Cell>
        {isEditing ? (
          <Flex gap="1" align="center">
            <Button
              variant="outline"
              size="2"
              onClick={handleDiscard}
              disabled={isSaving}
            >
              {t('cancel')}
            </Button>
            <Button
              size="2"
              variant="solid"
              onClick={handleSave}
              disabled={isSaving}
            >
              {t('save')}
            </Button>
          </Flex>
        ) : (
          <Flex gap="1" align="center">
            <DeleteFile item={file} setItem={setEditingId} />
            <Tooltip content={t('editFileTooltip')}>
              <IconButton
                variant="outline"
                size="2"
                color="gray"
                onClick={() => setEditingId(file.id)}
              >
                <i className="fa-light fa-pen-to-square fa-lg" />
              </IconButton>
            </Tooltip>
          </Flex>
        )}
      </Table.Cell>
    </Table.Row>
  );
};
